/**
 * wallet — formatting helpers (F-009.3).
 *
 * L'API serializza i cents BigInt come stringhe decimali e le date come ISO.
 * Questi helper accettano stringhe cents / date ISO. Mirror di
 * `features/admin-wallet/lib/format.ts` (scoping per-feature, niente coupling).
 */

/** Formatta cents (stringa/bigint/number) in euro localizzato, es. "1.234,56 €". */
export function centsToEuro(cents: string | bigint | number | null | undefined): string {
  if (cents === null || cents === undefined || cents === '') return '—';
  let value: number;
  if (typeof cents === 'bigint') value = Number(cents) / 100;
  else if (typeof cents === 'number') value = cents / 100;
  else {
    const parsed = Number(cents);
    if (Number.isNaN(parsed)) return '—';
    value = parsed / 100;
  }
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
}

/** Converte cents (stringa/bigint/number) in number per la matematica client-side. */
export function centsToNumber(cents: string | bigint | number | null | undefined): number {
  if (cents === null || cents === undefined || cents === '') return 0;
  if (typeof cents === 'bigint') return Number(cents);
  if (typeof cents === 'number') return cents;
  const parsed = Number(cents);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Formatta una data-ora ISO come `dd/mm/yyyy HH:mm` (locale italiano). */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso.slice(0, 16);
  }
}

/** Etichetta italiana per il `reason` di una transazione wallet. */
export const REASON_LABEL: Record<string, string> = {
  DEPOSIT_STRIPE: 'Ricarica',
  DEPOSIT_MANUAL: 'Ricarica manuale',
  DEBIT_PRATICA: 'Addebito pratica',
  REFUND_PRATICA: 'Rimborso',
  FIDO_ADJUSTMENT: 'Rettifica fido',
  RECONCILE: 'Riconciliazione',
};

export function reasonLabel(reason: string): string {
  return REASON_LABEL[reason] ?? reason;
}

/** Tagli di ricarica suggeriti (cents). UI-only; il minimo è imposto dall'API. */
export const RICARICA_PRESETS_CENTS = [5000, 10000, 25000, 50000] as const;
